import React, { useEffect, useState } from "react";
import TextArea from "../../components/data_entry/text_area";
import QuestionLabel from "./components/question_label";
import { QuestionProps } from "../survey";
import { RadioButton, RadioGroup } from "../../components/data_entry/radio";
import { Option, getOptionObjectFromText, getOptionText, getOptionOid } from "./question_helpers";
import styled from "styled-components";
import nextId from "react-id-generator";
import InputQuestionWrapper from "../../components/base_ui/higher_order_components/questions/input_question_wrapper";
import SelectQuestionWrapper from "../../components/base_ui/higher_order_components/questions/select_question_wrapper";

export interface Customizations {
  prompt: string;
  optional?: boolean;
  description?: string;
  placeholder?: string;
  maxValue?: number;
  followUp?: {
    qid: string;
    whenGreaterThan: number;
    prompt: string;
    description?: string;
    options: (string | Option)[];
  };
}

export interface Answer {
  value: string;
  selectedFollowUpOption?: string | Option;
}

const StyledFollowUp = styled.div`
  margin-top: 2em;
`;

const NumberErrorLabel = styled.div`
  color: red;
  margin-top: 1em;
`;

export default function SurveyQuestionNumberInput({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  showValidation,
  showRefreshedComponents = true,
}: QuestionProps<Answer, Customizations>) {
  const [showNumberError, setShowNumberError] = useState(false);
  const [numericInputErrorMessage, setNumericInputErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [followUpQuestionErrorMessage, setfollowUpQuestionErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [touched, setTouched] = useState<boolean>(false);
  const [showMaxCharactersError, setShowMaxCharactersError] = useState(false);
  const label = customizations.prompt;
  const id = "number-input-" + nextId();
  const optional = typeof customizations.optional === "undefined" ? false : customizations.optional;
  const description = customizations.description;
  const placeholder = customizations.placeholder;
  const currentAnswerValue = currentAnswer ? currentAnswer.value : undefined;
  const selectedFollowUpOption = currentAnswer ? currentAnswer.selectedFollowUpOption : undefined;
  const followUp = customizations.followUp;
  const maxValue = customizations.maxValue;

  const isFollowUpQuestionAnswerValid = (): boolean => {
    return (
      !followUp ||
      // This is a bit complicated - If it has a follow up, we should make sure it either is showing and answered, or not showing at all.
      (!!followUp &&
        ((shouldShowFollowUp(currentAnswerValue) && !!selectedFollowUpOption) ||
          !shouldShowFollowUp(currentAnswerValue)))
    );
  };
  const answerIsValid =
    optional ||
    (typeof currentAnswerValue !== "undefined" &&
      currentAnswerValue !== "" &&
      isFollowUpQuestionAnswerValid());

  const numericInputAnswerIsValid = optional || currentAnswerValue;
  const followUpQuestionAnswerIsValid = isFollowUpQuestionAnswerValid();

  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  useEffect(() => {
    setNumericInputErrorMessage(
      !numericInputAnswerIsValid ? "Please complete the required field." : undefined,
    );
  }, [numericInputAnswerIsValid]);

  useEffect(() => {
    setfollowUpQuestionErrorMessage(
      !followUpQuestionAnswerIsValid ? "Please complete the required field." : undefined,
    );
  }, [followUpQuestionAnswerIsValid]);

  function shouldShowFollowUp(currentAnswerValue: string | undefined): boolean {
    return (
      !!followUp &&
      !!currentAnswerValue &&
      Number.isInteger(parseInt(currentAnswerValue)) &&
      Number(currentAnswerValue) > followUp.whenGreaterThan
    );
  }

  const deprecatedNumberInput = (
    <div className="number-input">
      <QuestionLabel
        label={label}
        htmlFor={id}
        description={description}
        questionIsOptional={optional}
      />
      <TextArea
        data-test="number-input-input"
        id={id}
        placeholder={placeholder ? placeholder : optional ? undefined : "Please only enter numbers"}
        rows={1}
        questionIsOptional={optional}
        onChange={({ currentTarget: { value: updatedResponse } }) => {
          const numberFormat = /^\d*\.?\d*$/;
          const numberIsProperFormat = numberFormat.test(updatedResponse.trim());
          const numberIsWithinValidRange = maxValue ? parseInt(updatedResponse) <= maxValue : true;
          if (numberIsProperFormat && numberIsWithinValidRange) {
            updateAnswer({ value: updatedResponse });
            setShowNumberError(false);
            setShowMaxCharactersError(false);
          } else if (!numberIsProperFormat) {
            setShowNumberError(true);
            updateAnswer({ value: "" });
          } else if (!numberIsWithinValidRange) {
            setShowMaxCharactersError(true);
            updateAnswer({ value: "" });
          }
        }}
        value={currentAnswerValue ? currentAnswerValue : ""}
      />
      {showNumberError && <NumberErrorLabel>Numbers only please.</NumberErrorLabel>}
      {showMaxCharactersError && (
        <NumberErrorLabel>Please enter a number from 0 - {maxValue}.</NumberErrorLabel>
      )}
      {followUp && currentAnswerValue && shouldShowFollowUp(currentAnswerValue) && (
        <StyledFollowUp>
          <QuestionLabel
            label={followUp.prompt}
            description={followUp.description}
            questionIsOptional={false}
          />
          <RadioGroup
            name="numberInputQuestion"
            value={getOptionText(selectedFollowUpOption)}
            onChange={(followUpValue) =>
              updateAnswer({
                value: currentAnswerValue,
                selectedFollowUpOption: getOptionObjectFromText(followUpValue, followUp.options),
              })
            }
          >
            {followUp.options.map((option) => {
              const optionText = getOptionText(option);
              return (
                <RadioButton
                  data-test="number-input-select-option"
                  key={optionText}
                  label={optionText || ""}
                  value={optionText}
                />
              );
            })}
          </RadioGroup>
        </StyledFollowUp>
      )}
    </div>
  );
  const numberInput = (
    <>
      <InputQuestionWrapper
        id={id}
        dataTest="number-input-input"
        supplementalQuestionPrompt={description && label}
        label={description ? "" : label}
        secondaryText={description}
        optional={optional}
        placeholder={placeholder}
        type="number"
        min={0}
        max={maxValue}
        value={currentAnswerValue ? currentAnswerValue : ""}
        onChange={({ currentTarget: { value: updatedResponse } }) => {
          if (!maxValue || (maxValue && maxValue < 100 && updatedResponse.length <= 2)) {
            updateAnswer({ value: updatedResponse });
          }
        }}
        onBlur={() => {
          setTouched(true);
        }}
        errorMessage={touched || showValidation ? numericInputErrorMessage : undefined}
      ></InputQuestionWrapper>
      {followUp && currentAnswerValue && shouldShowFollowUp(currentAnswerValue) && (
        <SelectQuestionWrapper
          dataTest="number-input-select-option"
          options={followUp.options.map((option) => ({
            id: getOptionOid(option) || "",
            label: getOptionText(option) || "",
          }))}
          onChange={(followUpValue) => {
            updateAnswer({
              value: currentAnswerValue,
              selectedFollowUpOption: getOptionObjectFromText(
                followUpValue.label as string,
                followUp.options,
              ),
            });
          }}
          optional={false}
          label={followUp.prompt}
          value={{
            id: selectedFollowUpOption ? getOptionOid(selectedFollowUpOption) : "",
            label: getOptionText(selectedFollowUpOption) || "",
          }}
          errorMessage={showValidation ? followUpQuestionErrorMessage : undefined}
        ></SelectQuestionWrapper>
      )}
    </>
  );

  return showRefreshedComponents ? numberInput : deprecatedNumberInput;
}
